import { $api } from '@/common/api';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { resetMessageInbox } from '@/composables/useMessageInbox';
import { resetPortalMessages } from '@/composables/usePortalMessages';
import { Organization, Patient, Practitioner, PractitionerRole, User } from '@/fhirworks';
import { useTimeoutFn } from '@vueuse/core';
import { isValid, parseISO } from 'date-fns';
import { isEmpty, upperFirst } from 'lodash';
import { defineStore } from 'pinia';
import VueCookies from 'vue-cookies';

/**
 * Dispatches a CustomEvent to notify browser extensions of authentication state changes.
 * This enables SSO with partner extensions using a secure one-time code exchange.
 *
 * Security model:
 * - We dispatch a one-time SSO code (not the actual token)
 * - The code expires in 30 seconds and can only be redeemed once
 * - Even if XSS steals the code, it's useless after first redemption
 * - 3rd parties call /api/auth/sso/redeem to verify the code server-side
 *
 * @param {string} eventType - The type of auth event: 'login', 'logout', or 'refresh'
 * @param {object|null} authInfo - Non-sensitive auth info (userId, accountId, timestamp)
 * @param {string|null} ssoCode - One-time SSO code for secure verification (optional)
 */
function dispatchAuthEvent(eventType, authInfo = null, ssoCode = null) {
    if ((eventType === 'login' || eventType === 'refresh') && !ssoCode) {
        return;
    }
    const event = new CustomEvent('bestnotes:auth', {
        detail: {
            type: eventType,
            timestamp: Date.now(),
            ssoCode: ssoCode, // One-time use, 30 second expiry
            ...(authInfo || {}),
        },
    });
    window.dispatchEvent(event);
}

/**
 * True only when a Location is *known* to be closed. A Location whose `status` is absent has
 * unknown status — the field has to be named in the getMe `_elements` clause to come back at all —
 * and is reported as open, so a payload that stops carrying `status` under-labels rather than
 * marking every location closed. `users.api.js` requests `Location.status`; the spec there pins it.
 *
 * Closed locations are deliberately still resolved and still offered in the switcher (#5195): the
 * user keeps working where they were and is shown a "Closed" chip prompting them to update their
 * primary location in user management, rather than being moved somewhere they didn't choose.
 *
 * @param {object} locationResource - A Location resource, possibly undefined.
 * @returns {boolean} True when the resource exists, has a status, and that status isn't 'active'.
 */
export function isKnownClosedLocation(locationResource) {
    return !!locationResource?.status && locationResource.status !== 'active';
}

/**
 * Builds the cached `brand` shape ({ id, name, resourceType, location }) from a brand-like and a
 * location-like object. Accepts either raw Aidbox resources (Location carries `name`) or the
 * switcher shapes built below (location carries `display`), so all three resolution branches in
 * `resolveActiveBrandLocation` share one definition of the shape.
 *
 * @param {object} brandLike - An Organization resource or a switcher brand ({ id, name, resourceType }).
 * @param {object} locationLike - A Location resource or a switcher location ({ id, display, ... }).
 * @returns {object} The brand/location object cached as `authStore.brand`.
 */
function toBrandLocation(brandLike, locationLike) {
    return {
        id: brandLike?.id,
        name: brandLike?.name,
        resourceType: brandLike?.resourceType,
        location: {
            id: locationLike.id,
            display: locationLike.display ?? locationLike.name,
            resourceType: locationLike.resourceType,
            scriptSurePracticeId: locationLike.scriptSurePracticeId,
            // Carried so the switcher and top bar can badge a closed location without refetching.
            closed: locationLike.closed ?? isKnownClosedLocation(locationLike),
        },
    };
}

/**
 * Resolves the default brand/location and the switcher's brand list from a user's Aidbox resources,
 * skipping closed (inactive) locations. A location is considered "closed" when its Location resource
 * has `status !== 'active'`.
 *
 * Resolution order:
 *   1. `practitionerResource.primaryLocation`, whether it is open or closed — a closed primary is
 *      kept and badged rather than swapped out, so the user stays where they chose to be and is
 *      prompted to update it in user management (#5195).
 *   2. When there is no primary at all: the first location of the first (name-sorted) brand that
 *      has one — i.e. the first entry the switcher itself would offer.
 *   3. Last resort: the first Location of any status, so the app has something to work with.
 *
 * Every location returned — the resolved one and each switcher entry — carries a `closed` flag so
 * the UI can badge it without a second fetch. This depends on `Location.status` being present in
 * the getMe payload; see `isKnownClosedLocation`.
 *
 * @param {Array} aidboxUserResources - The user's Aidbox resources (Organizations, Locations, PractitionerRoles, etc).
 * @param {object} practitionerResource - The Practitioner resource (source of primaryLocation/primaryLocationBrand).
 * @returns {{ brandLocation: (object|undefined), brands: Array }}
 */
export function resolveActiveBrandLocation(aidboxUserResources, practitionerResource) {
    const primaryLocation = practitionerResource?.primaryLocation;
    const primaryLocationBrand = practitionerResource?.primaryLocationBrand;

    const primaryLocationResource = aidboxUserResources.find((item) => item.resourceType === 'Location' && item.id === primaryLocation?.id);
    const hasPrimaryLocation = !!primaryLocationResource;

    // The primary location is honoured whether or not it is closed — a closed one is badged, not
    // swapped out (#5195). Only a missing primary falls back, preferring an open location.
    const location = hasPrimaryLocation
        ? primaryLocationResource
        : aidboxUserResources.find((item) => item.resourceType === 'Location' && !isKnownClosedLocation(item)) || aidboxUserResources.find((item) => item.resourceType === 'Location');

    const brand = aidboxUserResources.find((item) => item.id === primaryLocationBrand?.id) || aidboxUserResources.find((item) => item.id === location?.managingOrganization?.id);

    let brandLocation;
    let brands = [];

    let userBrands = aidboxUserResources.filter((item) => item.resourceType === 'Organization').sort((a, b) => (a.name > b.name ? 1 : -1));

    userBrands?.forEach((brandItem) => {
        let locations = [];
        aidboxUserResources
            .filter((item) => item.organization?.id === brandItem.id)
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            ?.forEach((item) => {
                const locationRef = item.location[0];
                // Find the full location resource to get extensions like scriptSurePracticeId
                const fullLocation = aidboxUserResources.find((res) => res.resourceType === 'Location' && res.id === locationRef.id);
                // Closed locations stay selectable — they carry `closed` so the switcher can badge them.
                locations.push({
                    id: locationRef.id,
                    display: locationRef.display,
                    resourceType: 'Location',
                    scriptSurePracticeId: fullLocation?.scriptSurePracticeId,
                    closed: isKnownClosedLocation(fullLocation),
                });
            });
        brands.push({ id: brandItem.id, name: brandItem.name, resourceType: 'Organization', locations: locations.sort((a, b) => (a.display > b.display ? 1 : -1)) });
    });

    if (primaryLocationBrand?.id && brand?.id && location?.id && hasPrimaryLocation) {
        brandLocation = toBrandLocation(brand, location);
    } else {
        // No primary to respect, so pick for the user: the first brand with an open location, and
        // only if none exists the first brand with any location at all.
        const defaultBrand = brands.find((item) => item.locations.some((loc) => !loc.closed)) || brands.find((item) => item.locations.length);
        if (defaultBrand) {
            const defaultLocation = defaultBrand.locations.find((loc) => !loc.closed) || defaultBrand.locations[0];
            brandLocation = toBrandLocation(defaultBrand, defaultLocation);
        } else if (location?.id) {
            // Last resort: the account has zero active locations but at least one (closed) location exists.
            brandLocation = toBrandLocation(brand, location);
        }
    }

    return { brandLocation, brands };
}

/**
 * Self-heals a cached brand whose selected location is no longer present in the current agency list
 * (e.g. the location was closed since the brand was cached). Returns the brand unchanged when the
 * cached location is still valid or there's nothing to reconcile against.
 *
 * @param {object} brand - The cached brand ({ id, name, resourceType, location: { id, ... } }).
 * @param {Array} userAgencies - The switcher agencies ([{ id, name, locations: [{ id, display, resourceType, scriptSurePracticeId }] }]).
 * @returns {object} The reconciled brand.
 */
export function reconcileBrandWithAgencies(brand, userAgencies) {
    if (!userAgencies || !userAgencies.length || !brand?.location?.id) {
        return brand;
    }

    const stillPresent = userAgencies.some((agency) => (agency.locations || []).some((loc) => loc.id === brand.location.id));
    if (stillPresent) {
        return brand;
    }

    const toLocation = (loc) => ({
        id: loc.id,
        display: loc.display,
        resourceType: loc.resourceType,
        scriptSurePracticeId: loc.scriptSurePracticeId,
    });

    // Prefer the first location of the brand's own agency, if it still has any.
    const currentAgency = userAgencies.find((agency) => agency.id === brand.id);
    if (currentAgency?.locations?.length) {
        return { ...brand, location: toLocation(currentAgency.locations[0]) };
    }

    // Otherwise switch to the first agency that still has a location.
    const fallbackAgency = userAgencies.find((agency) => agency.locations?.length);
    if (fallbackAgency) {
        return {
            ...brand,
            id: fallbackAgency.id,
            name: fallbackAgency.name,
            location: toLocation(fallbackAgency.locations[0]),
        };
    }

    return brand;
}

// Feature flag claims decoded from the token (#5524). `fallback` is what a missing/undecodable
// claim reads as — null for restrictedFeatures so the gate fails closed, [] for the marker lists.
function featureClaim(token, name, fallback) {
    if (!token) return fallback;
    try {
        const claim = JSON.parse(atob(token.split('.')[1]))[name];
        return Array.isArray(claim) ? claim : fallback;
    } catch {
        return fallback;
    }
}

export const useAuthStore = defineStore('auth', {
    state() {
        return {
            token: '',
            user: {},
            practitioner: {},
            practitionerRoles: [],
            patient: {},
            portalViewingPatientId: null,
            relatedPersons: [],
            permissions: [],
            organizationAccount: {},
            account: {},
            brand: {},
            userAgencies: [],
            accountsCount: null,
            lockoutLastActivityTime: new Date(),
            lockoutPage: null,
            tokenRefreshTimeMins: 12,
            lastTokenRefreshTime: null,
            loginStatus: {
                loading: false,
                success: false,
                errors: [],
            },
            _isLoggingIn: false,
            _redirectingToAuth: false,
        };
    },
    getters: {
        isAuthenticated: (state) => !!state.token,
        isLockedOut: (state) => !state.token && !isEmpty(state.user),
        hasAccessGroup: (state) => (groups) => {
            if (!groups) return true;

            let assignedAccessGroupIds = state.user.user.accessGroups?.map((group) => group.id) || [];
            if (!Array.isArray(groups)) {
                groups = [groups];
            }
            const found = groups.some((searchGroup) => assignedAccessGroupIds.includes(searchGroup.id));
            return found;
        },
        hasPermission: (state) => (permissions) => {
            // we want to accomplish arrays and a single string.  If none is input.  filter out those below
            if (!Array.isArray(permissions)) {
                permissions = [permissions].filter((perm) => perm);
            }
            return permissions.some((permission) => state.permissions.includes(permission));
        },
        aidboxPersonResource: (state) => {
            return state.practitioner?.id ? state.practitioner : state.patient;
        },
        aidboxPersonReference() {
            return { id: this.aidboxPersonResource?.id, resourceType: this.aidboxPersonResource?.resourceType, display: this.aidboxPersonResource?.fullName };
        },
        fullName() {
            return (this.aidboxPersonResource?.fullName || '').trim();
        },
        firstName() {
            return (this.aidboxPersonResource?.firstName || '').trim();
        },
        lockoutTime: (state) => (state.organizationAccount?.sessionTimeOut ? state.organizationAccount?.sessionTimeOut : 5) * 60000,
        timeSlot: (state) => (state.organizationAccount?.calendarTimeSlot ? state.organizationAccount?.calendarTimeSlot : 30),
        tokenRefreshTime: (state) => state.tokenRefreshTimeMins * 60000,
        clientVocab: (state) => (state.organizationAccount?.clientVocab ? upperFirst(state.organizationAccount?.clientVocab) : 'Patient'),
        inputDateFormat: (state) => (state.organizationAccount?.inputDateFormat ? state.organizationAccount?.inputDateFormat : 'MM/dd/yyyy'),
        outputDateFormat: (state) => (state.organizationAccount?.outputDateFormat ? state.organizationAccount?.outputDateFormat : 'MMM do yyyy'),
        outputTimeFormat: (state) => (state.militaryTime ? ' kk:mm' : ' h:mm a'),
        militaryTime: (state) => (state.organizationAccount?.militaryTime ? state.organizationAccount?.militaryTime : false),
        portalEnabled: (state) => state.account?.portalEnabled ?? false,
        messagingEnabled: (state) => state.account?.messagingEnabled ?? false,
        clientTextingEnabled: (state) => state.account?.clientTextingEnabled ?? false,
        scriptSureEnabled: (state) => state.account?.scriptSureEnabled ?? false,
        ellkayEnabled: (state) => state.account?.ellkayEnabled ?? false,
        showSwitchAccount: (state) => state.accountsCount > 1,
        fhirApiUri: (state) => state.account.fhirApiUri,
        isBnSupport: (state) => {
            if (!state.token) return false;
            try {
                const tokenData = JSON.parse(atob(state.token.split('.')[1]));
                return !!tokenData.isSupport;
            } catch {
                return false;
            }
        },
        isBnSupportUser: (state) => state.user.user?.id === 'bnsupport-user',
        bnSupportRoles: (state) => {
            if (!state.token) return [];
            let tokenData = JSON.parse(atob(state.token.split('.')[1]));
            return tokenData.bnSupportRoles || [];
        },
        hasBnSupportRole: (state) => (role) => {
            if (!state.token) return false;
            let tokenData = JSON.parse(atob(state.token.split('.')[1]));
            return (tokenData.bnSupportRoles || []).includes(role);
        },
        // null (not []) when the claim is missing or undecodable so useFeatureFlags fails closed.
        restrictedFeatures: (state) => featureClaim(state.token, 'restrictedFeatures', null),
        newFeatures: (state) => featureClaim(state.token, 'newFeatures', []),
        previewFeatures: (state) => featureClaim(state.token, 'previewFeatures', []),

        // portal getters
        portalPatientItems: (state) => {
            // Include the logged-in user as the first item
            const items = [];
            if (state.patient?.id) {
                items.push({
                    relationship: null,
                    patient: state.patient,
                    display: 'Myself',
                });
            }
            // Add all related persons
            items.push(...state.relatedPersons);
            return items;
        },
        portalViewingPatient: (state) => {
            // Check if viewing self
            if (state.portalViewingPatientId === state.patient?.id) {
                return state.patient;
            }
            // Otherwise find in related persons
            return state.relatedPersons.find((item) => item.patient?.id === state.portalViewingPatientId)?.patient;
        },
        patientInquiryStatus() {
            return this.portalViewingPatient?.inquiryStatus;
        },
        patientInquiryAccepted() {
            return this.portalViewingPatient?.inquiryStatus === 'accepted' || !this.portalViewingPatient?.inquiryStatus;
        },
        hasMultipleRelatedPersons: (state) => state.relatedPersons?.length > 0,
    },
    actions: {
        setToken(token, eventType = 'login', ssoCode = null) {
            // Store token in memory only - refresh token is in HttpOnly cookie
            this.token = token;
            VueCookies.set('bn_session_active', '1', '1d');

            // Notify browser extensions of auth state change (for SSO)
            // The ssoCode is provided by the server in the login/refresh response
            try {
                const tokenData = JSON.parse(atob(token.split('.')[1]));
                const authInfo = {
                    authenticated: true,
                    userId: tokenData.box_user,
                    userSystemId: tokenData.sub,
                    userEmail: tokenData.username,
                    accountId: tokenData.aud?.replace('account/', '') || null,
                    permissions: tokenData.permissions,
                };

                dispatchAuthEvent(eventType, authInfo, ssoCode);
            } catch {
                // If token parsing fails, still dispatch event with minimal info
                dispatchAuthEvent(eventType, { authenticated: true }, ssoCode);
            }
        },
        removeToken() {
            this.token = '';

            // Notify browser extensions of logout (for SSO)
            dispatchAuthEvent('logout', { authenticated: false }, null);
        },
        setUser(user) {
            if (user.user && !(user.user instanceof User)) {
                user.user = new User(user.user);
            }

            VueCookies.set('user', JSON.stringify(user));
            this.user = user;
        },
        removeUser() {
            VueCookies.remove('user');
            this.user = {};
        },
        setPractitioner(practitioner) {
            if (practitioner && !(practitioner instanceof Practitioner)) {
                practitioner = new Practitioner(practitioner);
            }

            VueCookies.set('practitioner', JSON.stringify(practitioner));
            this.practitioner = practitioner;
        },
        removePractitioner() {
            VueCookies.remove('practitioner');
            this.practitioner = {};
        },
        // for Patient Portal
        setPatient(patient) {
            if (patient && !(patient instanceof Patient)) {
                patient = new Patient(patient);
            }

            VueCookies.set('patient', JSON.stringify(patient));
            this.patient = patient;
        },
        removePatient() {
            VueCookies.remove('patient');
            this.patient = {};
        },
        setPortalViewingPatientId(patientId) {
            VueCookies.set('portalViewingPatientId', patientId);
            this.portalViewingPatientId = patientId;
        },
        removePortalViewingPatientId() {
            VueCookies.remove('portalViewingPatientId');
            this.portalViewingPatientId = null;
        },
        setPermissions(permissions) {
            if (typeof permissions === 'string') {
                permissions = JSON.parse(permissions);
            }

            this.permissions = Object.values(permissions);
        },
        removePermissions() {
            this.permissions = [];
        },
        setPractitionerRoles(practitionerRoles) {
            if (typeof practitionerRoles === 'string') {
                practitionerRoles = JSON.parse(practitionerRoles);
            }

            localStorage.setItem('practitionerRoles', JSON.stringify(practitionerRoles));
            this.practitionerRoles = practitionerRoles;
        },
        removePractitionerRoles() {
            localStorage.removeItem('practitionerRoles');
            this.practitionerRoles = [];
        },
        setAccountData(account) {
            VueCookies.set('account', JSON.stringify(account));
            this.account = account;
        },
        removeAccount() {
            VueCookies.remove('account');
            this.account = {};
        },
        setBrand(brand) {
            const activeBrand = {
                id: brand.id,
                name: brand.display || brand.name,
                location: brand.location,
            };
            VueCookies.set('brand', JSON.stringify(activeBrand));
            this.brand = activeBrand;
        },
        removeBrand() {
            VueCookies.remove('brand');
            this.brand = {};
        },
        setUserAgencies(userAgencies) {
            localStorage.setItem('userAgencies', JSON.stringify(userAgencies));
            this.userAgencies = userAgencies;
        },
        removeUserAgencies() {
            localStorage.removeItem('userAgencies');
            this.userAgencies = [];
        },
        setOrganizationAccount(account) {
            if (!(account instanceof Organization)) {
                account = new Organization(account);
            }
            VueCookies.set('organizationAccount', JSON.stringify(account));
            this.organizationAccount = account;
        },
        removeOrganizationAccount() {
            VueCookies.remove('organizationAccount');
            this.organizationAccount = {};
        },
        setAccountDetails(details) {
            if (!Array.isArray(details)) details = [details];
            details.forEach((item) => {
                if (item.value) {
                    if (item.key === 'name') {
                        this.account.name = item.value;
                        VueCookies.set('account', JSON.stringify(this.account));

                        this.organizationAccount.name = item.value;
                        VueCookies.set('organizationAccount', JSON.stringify(this.organizationAccount));
                        return;
                    }
                    if (item.key === 'logo') {
                        this.account.logo = item.value;
                        VueCookies.set('account', JSON.stringify(this.account));
                    }
                    this.organizationAccount[item.key] = item.value;
                    VueCookies.set('organizationAccount', JSON.stringify(this.organizationAccount));
                }
            });
        },
        updateAgencyDetails(agencyId, details) {
            if (!Array.isArray(details)) details = [details];
            let agenciesChanged = false;
            details.forEach((item) => {
                if (item.value && item.key === 'name') {
                    // Guard on brand?.id being truthy (not just === agencyId) so an empty brand {}
                    // doesn't match an undefined agencyId via the undefined === undefined trap.
                    if (this.brand?.id && this.brand.id === agencyId) {
                        this.brand.name = item.value;
                        VueCookies.set('brand', JSON.stringify(this.brand));
                    }
                    // userAgencies updates unconditionally for the matched agency, even when it
                    // isn't the active brand — so the selector dropdown label refreshes too.
                    const agencyEntry = this.userAgencies.find((a) => a.id === agencyId);
                    if (agencyEntry) {
                        agencyEntry.name = item.value;
                        agenciesChanged = true;
                    }
                }
            });
            if (agenciesChanged) {
                localStorage.setItem('userAgencies', JSON.stringify(this.userAgencies));
            }
        },
        setAccountsCount(count) {
            localStorage.setItem('accounts-count', count);
            this.accountsCount = count;
        },
        removeAccountsCount() {
            localStorage.removeItem('accounts-count');
            this.accountsCount = null;
        },
        setLastTokenRefreshTime(time) {
            if (isValid(time)) {
                localStorage.setItem('lastTokenRefreshTime', time.toISOString());
            }
            this.lastTokenRefreshTime = time;
        },
        removeLastTokenRefreshTime() {
            localStorage.removeItem('lastTokenRefreshTime');
            this.lastTokenRefreshTime = null;
        },
        setLockoutPage(page) {
            localStorage.setItem('lockoutPage', page);
            this.lockoutPage = page;
        },
        removeLockoutPage() {
            localStorage.removeItem('lockoutPage');
            this.lockoutPage = null;
        },
        /**
         * If credentials are provided then login, else...
         * If the user is already authenticated then set the data in the store
         *
         * @param commit
         * @param credentials
         * @returns {Promise<void>}
         */
        async login(credentials) {
            try {
                this.loginStatus.loading = true;
                this.loginStatus.success = false;
                this.loginStatus.errors = [];
                this._isLoggingIn = true;

                await this.clearAuth(); // Attempt to logout the current user before logging in
                await this.getAuthToken(credentials);
                // await this.logNotice({ summary: 'Login Successful' });

                this.loginStatus.loading = false;
                this.loginStatus.success = true;
            } catch (errs) {
                this._isLoggingIn = false;
                await this.clearAuth();
                return Promise.reject(errs);
            }
        },

        async refreshCredentials() {
            if (!VueCookies.get('bn_session_active')) {
                this.clearClientAuthData();
                return;
            }

            // Restore cached data from cookies/localStorage
            let user = VueCookies.get('user');
            let practitioner = VueCookies.get('practitioner');
            let patient = VueCookies.get('patient');
            let account = VueCookies.get('account');
            let organizationAccount = VueCookies.get('organizationAccount');
            let brand = VueCookies.get('brand');
            let practitionerRoles = localStorage.getItem('practitionerRoles');
            let lastTokenRefreshTime = localStorage.getItem('lastTokenRefreshTime');
            let accountsCount = localStorage.getItem('accounts-count');
            let userAgencies;
            if (localStorage.getItem('userAgencies')) {
                userAgencies = JSON.parse(localStorage.getItem('userAgencies'));
            }
            let lockoutPage;
            if (localStorage.getItem('lockoutPage')) {
                lockoutPage = localStorage.getItem('lockoutPage');
            }

            // Check for Cypress test token first (bypasses HttpOnly cookie flow)
            let cypressToken = localStorage.getItem('cypress_token');
            if (cypressToken && window.Cypress) {
                this.setToken(cypressToken, 'refresh');
                let tokenData = JSON.parse(atob(cypressToken.split('.')[1]));
                if (tokenData?.permissions) {
                    this.setPermissions(tokenData.permissions);
                }
            } else {
                // Try to get a new access token using the HttpOnly refresh cookie
                try {
                    let res;
                    if (window.page === 'ehr') {
                        res = await $api.auth.refreshToken({ params: { jwtTokenAccount: account.id } });
                    } else {
                        res = await $api.auth.refreshTokenPortal({ params: { jwtTokenAccount: account.id } });
                    }

                    if (res.data.token) {
                        this.setToken(res.data.token, 'refresh');

                        let tokenData = JSON.parse(atob(res.data.token.split('.')[1]));
                        if (tokenData?.permissions) {
                            this.setPermissions(tokenData.permissions);
                        }
                    }
                } catch (error) {
                    // No valid refresh token, and user is not locked out - user needs to log in again
                    if (!(error?.response?.status === 401 && user && lockoutPage)) {
                        // Clear any stale cached data
                        this.clearAuth();
                        return;
                    }
                }
            }

            // Restore cached user/account data
            if (user) {
                this.setUser(user);
            }
            if (lastTokenRefreshTime) {
                this.setLastTokenRefreshTime(parseISO(lastTokenRefreshTime));
            }
            if (practitioner) {
                this.setPractitioner(practitioner);
            }
            if (patient) {
                this.setPatient(patient);
            }
            if (practitionerRoles) {
                this.setPractitionerRoles(practitionerRoles);
            }
            if (account) {
                this.setAccountData(account);
            }
            if (organizationAccount) {
                this.setOrganizationAccount(organizationAccount);
            }
            if (brand && account) {
                // Cheap synchronous pass for a cached location that has genuinely vanished from the
                // agency list (deleted, or access revoked) — a cookie/localStorage desync, since the
                // two expire independently. Closure alone no longer removes a location from the
                // list, so this is not the closed-location path; the re-resolve below is.
                this.setBrand(reconcileBrandWithAgencies(brand, userAgencies));
            }
            if (userAgencies && account) {
                this.setUserAgencies(userAgencies);
            }
            if (accountsCount) {
                this.setAccountsCount(accountsCount);
            }
            if (lockoutPage) {
                this.setLockoutPage(lockoutPage);
            }

            // The cached brand and userAgencies are always written together by getMeUser(), so the
            // cached `closed` flags are only as fresh as the last login. Re-resolve from live data
            // on reload so a location closed mid-session is badged without requiring a fresh login
            // — the badge is the whole prompt, so a stale one defeats the feature. Non-fatal: on
            // failure we keep the restored cache and carry on booting.
            if (account && this.token && window.page === 'ehr') {
                try {
                    await this.getMeUser();
                } catch (error) {
                    console.error('Error re-resolving user resources on reload:', error);
                }
            }
        },

        async getAuthToken(credentials) {
            // If account exists send it, so we can get a token tied to the account
            let config = this.account.id ? { params: { jwtTokenAccount: this.account.id }, errorHandle: false } : { errorHandle: false };
            let res;

            if (window.page === 'ehr') {
                res = await $api.auth.login(credentials, config);
            } else {
                res = await $api.auth.portalLogin(credentials, config);
            }
            let token = res?.data?.token;
            let ssoCode = res?.data?.ssoCode || null;

            let tokenData = JSON.parse(atob(token.split('.')[1]));

            this.setToken(token, 'login', ssoCode); // Set the token and dispatch SSO event

            if (tokenData?.permissions) {
                this.setPermissions(tokenData.permissions);
            }

            let now = new Date();
            this.setLastTokenRefreshTime(now);
        },

        async refreshAuthToken() {
            if (!this.isAuthenticated) {
                return;
            }

            let existingTokenData = JSON.parse(atob(this.token.split('.')[1]));
            if (existingTokenData?.aud && !existingTokenData?.aud?.includes('account/' + this.account.id)) {
                return;
            }

            let config = this.account.id ? { params: { jwtTokenAccount: this.account.id } } : null;

            const maxRetries = 2;
            let lastError;
            for (let attempt = 0; attempt <= maxRetries; attempt++) {
                try {
                    let res;
                    if (window.page === 'ehr') {
                        res = await $api.auth.refreshToken(config);
                    } else {
                        res = await $api.auth.refreshTokenPortal(config);
                    }
                    let token = res.data.token;
                    let ssoCode = res.data.ssoCode || null;

                    if (token) {
                        let now = new Date();
                        let tokenData = JSON.parse(atob(token.split('.')[1]));
                        const eventType = this._isLoggingIn ? 'login' : 'refresh';
                        this._isLoggingIn = false;
                        this.setToken(token, eventType, ssoCode);
                        if (tokenData?.permissions) {
                            this.setPermissions(tokenData.permissions);
                        }
                        this.setLastTokenRefreshTime(now);
                    }
                    return;
                } catch (error) {
                    lastError = error;
                    // 401 means the refresh token itself is invalid — retrying won't help
                    if (error?.response?.status === 401) {
                        throw error;
                    }
                    if (attempt < maxRetries) {
                        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
                    }
                }
            }
            throw lastError;
        },

        async getMeUser() {
            let aidboxUserResources;

            let tokenData = JSON.parse(atob(this.token.split('.')[1]));

            let apiUser = {
                email: tokenData.username,
                id: tokenData.sub,
                isBnSupport: tokenData?.isSupport,
                videraEnabled: tokenData?.videraEnabled,
                videraUserId: tokenData?.videraUserId,
            };

            if (window.page === 'ehr') {
                aidboxUserResources = await $api.users.getMe(apiUser.id);
            } else {
                aidboxUserResources = await $api.users.getMePortal(apiUser.id);
            }

            let storeUser = { apiUser: apiUser };

            if (window.page === 'ehr') {
                let practitioner, practitionerRoles;

                if (aidboxUserResources?.length) {
                    let userResource = aidboxUserResources.find((entry) => entry.resourceType === 'User');

                    let practitionerResource = aidboxUserResources.find((entry) => entry.resourceType === 'Practitioner');
                    let practitionerRoleResources = aidboxUserResources.filter((entry) => entry.resourceType === 'PractitionerRole');

                    if (userResource) {
                        storeUser.user = userResource;
                    }
                    if (practitionerResource) {
                        practitioner = practitionerResource;
                    }
                    if (practitionerRoleResources) {
                        if (!(practitionerRoleResources[0] instanceof PractitionerRole)) {
                            practitionerRoleResources = practitionerRoleResources.map((role) => new PractitionerRole(role));
                        }

                        practitionerRoles = practitionerRoleResources.map((role) => ({
                            programId: role.healthcareService[0]?.id,
                            locationId: role.location[0]?.id,
                            inquiry: role?.healthcareServiceAccess[0]?.inquiry,
                            active: role?.healthcareServiceAccess[0]?.active,
                            past: role?.healthcareServiceAccess[0]?.past,
                            onlyCareTeam: role?.healthcareServiceAccess[0]?.onlyCareTeam,
                        }));

                        practitionerRoleResources.forEach((role) => {
                            role.healthcareService.forEach((hcs) => {
                                practitionerRoles.push({
                                    programId: hcs.id,
                                    locationId: role.location[0].id,
                                    inquiry: role?.healthcareServiceAccess.find((hsa) => hsa.healthcareService === hcs.id)?.inquiry,
                                    active: role?.healthcareServiceAccess.find((hsa) => hsa.healthcareService === hcs.id)?.active,
                                    past: role?.healthcareServiceAccess.find((hsa) => hsa.healthcareService === hcs.id)?.past,
                                    onlyCareTeam: role?.healthcareServiceAccess.find((hsa) => hsa.healthcareService === hcs.id)?.onlyCareTeam,
                                });
                            });
                        });

                        const { brandLocation, brands } = resolveActiveBrandLocation(aidboxUserResources, practitionerResource);

                        if (brandLocation) {
                            this.setBrand(brandLocation);
                            this.setUserAgencies(brands);
                        }
                    }
                } else if (storeUser.apiUser.isBnSupport) {
                    let location = null;

                    let locations = await $httpFhirApi.get('Location?status=active');
                    if (locations.status == 200) {
                        let locationsData = locations.data;
                        // Done for dev purposes otherwise 1st record will be used
                        let devLocation = locationsData.find((item) => item.id === 'dev-location');
                        if (!devLocation) {
                            devLocation = locationsData[0];
                        }
                        // Extract the location, brand and healthcareService from the devLocation
                        location = devLocation;
                    }

                    // Call to setBrand need to make autho stuff work?
                    this.setBrand({
                        id: location.managingOrganization?.id,
                        name: location.managingOrganization?.display,
                        location: { id: location.id, display: location.name, resourceType: location.resourceType },
                    });

                    storeUser.user = { id: 'bnsupport-user' };
                    practitioner = { id: 'bnsupport-practitioner', name: [{ use: 'official', given: ['bnsupport'] }] };
                    practitionerRoles = null;
                }

                this.setUser(storeUser); // Set the token before calling the getMe api endpoint
                this.setPractitioner(practitioner);
                this.setPractitionerRoles(practitionerRoles);
            } else {
                // Is portal user
                let patient;

                if (aidboxUserResources?.length) {
                    let userResource = aidboxUserResources.find((entry) => entry.resourceType === 'User');
                    let patientResource = aidboxUserResources.find((entry) => entry.resourceType === 'Patient');

                    if (userResource) {
                        storeUser.user = userResource;
                    }
                    if (patientResource) {
                        patient = patientResource;
                    }

                    this.setUser(storeUser); // Set the token before calling the getMe api endpoint
                    this.setPatient(patient);
                }
            }
        },

        async clearRefreshToken() {
            try {
                if (window.page === 'ehr') {
                    await $api.auth.logout();
                } else {
                    await $api.auth.logoutPortal();
                }
            } catch {
                // Ignore errors - we're logging out anyway
            }
        },

        clearClientAuthData(switchingAccount = false) {
            if (!switchingAccount) {
                this.removeToken();
                this.removeUser();
                // account needs to be here so we don't get logged out to login page when switching accounts
                this.removeAccount();
                this.removeOrganizationAccount();
                this.removeAccountsCount();
                this.removePractitioner();
                this.removePractitionerRoles();
                this.removePatient();
                VueCookies.remove('bn_session_active');
            }
            this.removeBrand();
            this.removeUserAgencies();
            this.removePermissions();
            this.removeLockoutPage();
            this.removeLastTokenRefreshTime();
            localStorage.removeItem('lockoutTriggered');
            // Module-scope state in a composable outlives this store, and signing out is pure SPA
            // state clearing with no reload — so the next user in this tab would inherit the
            // previous one's conversations. Runs on the switching-account path too: the threads
            // belong to the account, not just to the user (#5331). The portal's list is the same
            // shape and matters more, because the device is often shared (#5333).
            resetMessageInbox();
            resetPortalMessages();
        },

        async clearAuth(switchingAccount = false) {
            // End a live telehealth call before the credentials go. The host is
            // leaving, and the dock otherwise survives logout — it was still
            // running, mid-call, after logging back in.
            //
            // Runs first because ending the meeting is an authenticated request,
            // and is imported lazily because the telehealth store reaches back
            // to this one through its API layer; a static import would close
            // that circle.
            try {
                const { useTelehealthSessionStore } = await import('@/stores/telehealthSession');
                const telehealth = useTelehealthSessionStore();
                if (telehealth.open) await telehealth.endSession();
            } catch {
                // Tidying up the call must never block signing out.
            }

            // Call logout endpoint to clear the HttpOnly refresh token cookie
            if (!switchingAccount && this.token) {
                await this.clearRefreshToken();
            }

            this.clearClientAuthData(switchingAccount);
        },

        acquireAuthRedirect() {
            if (this._redirectingToAuth) return false;
            this._redirectingToAuth = true;
            return true;
        },

        resetAuthRedirect() {
            this._redirectingToAuth = false;
        },

        resetLockoutActivityTime() {
            let time = new Date();
            localStorage.setItem('lockoutLastActivityTime', time.toISOString());
            this.lockoutLastActivityTime = time;
        },

        setLockoutLastActivityTime(time) {
            this.lockoutLastActivityTime = time;
        },

        async lockScreen(lockoutPage) {
            this.setLockoutLastActivityTime(null);

            // Create a new object using the lockout page from state or the provided one
            lockoutPage = this.lockoutPage || lockoutPage;

            if (!lockoutPage || lockoutPage.includes('lockout')) {
                lockoutPage = '/dashboard';
            }

            this.setLockoutPage(lockoutPage);

            localStorage.setItem('lockoutTriggered', Date.now().toString());

            // allow time for background requests to complete (save state) before removing token
            // there may be a better way to do this... Justin?
            useTimeoutFn(() => {
                this.clearRefreshToken();
                this.removeToken();
            }, 3000);
        },

        async unlockScreen(password) {
            this.resetLockoutActivityTime();
            await this.getAuthToken({
                email: this.user.apiUser.email,
                password: password,
            });
            if (!this.isLockedOut) {
                this.removeLockoutPage();
                localStorage.removeItem('lockoutTriggered');
            }
        },

        async setAccount(account) {
            this.setAccountData(account);
            await this.refreshAuthToken();
            let organizationAccount = await $httpFhirApi.get('Organization/' + account.id);
            if (organizationAccount?.data) {
                this.setOrganizationAccount(organizationAccount.data);
            }
        },
        async requestPasswordResetAuth(data) {
            return await $api.auth.requestResetPassword(data);
        },
        async requestPasswordResetPortalAuth(data) {
            return await $api.auth.requestResetPasswordPortal(data);
        },
        async passwordResetPortalAuth(data) {
            return await $api.auth.resetPasswordPortal(data);
        },
        async passwordResetAuth(data) {
            return await $api.auth.resetPassword(data);
        },

        async refreshRelatedPersons() {
            if (!this.patient?.id) {
                this.relatedPersons = [];
                return;
            }

            try {
                const response = await $httpFhirApi.get(`/BN_Relationship?related=${this.patient.id}&portalAccess=true&active=true&_include=BN_Relationship:subject`);

                const relationships = response.data?.filter((item) => item.resourceType === 'BN_Relationship') || [];
                const patients = response.data?.filter((item) => item.resourceType === 'Patient') || [];

                this.relatedPersons = relationships.map((rel) => {
                    const relatedPatient = patients.find((p) => p.id === rel.subject?.id);
                    return {
                        relationship: rel,
                        patient: relatedPatient instanceof Patient ? relatedPatient : new Patient(relatedPatient),
                        display: relatedPatient?.fullName || 'Unknown',
                    };
                });
            } catch (error) {
                console.error('Error refreshing related persons:', error);
                this.relatedPersons = [];
            }
        },

        async loadRelatedPersons() {
            // Refresh the related persons list
            await this.refreshRelatedPersons();

            // Set initial viewing patient if not already set
            if (isEmpty(this.portalViewingPatient) && this.relatedPersons.length >= 0) {
                // Priority: accepted patient (self) > accepted related > pending related > any patient (self)
                const viewingPatientId =
                    (this.patient?.inquiryStatus === 'accepted' && this.patient?.id) ||
                    this.relatedPersons.find((item) => item.patient?.inquiryStatus === 'accepted')?.patient?.id ||
                    this.relatedPersons.find((item) => item.patient?.inquiryStatus === 'pending')?.patient?.id ||
                    this.patient?.id;

                this.setPortalViewingPatientId(viewingPatientId);
            }
        },
    },
});
